/**
 * Brand Voices API Hooks
 *
 * React Query hooks for brand voice CRUD operations with plan limit enforcement.
 * Phase 43-02: Multi Brand Voices
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { getEdgeFunctionUrl } from './edge-function-url';
import { useCurrentCompany } from './companies';
import { useEntitlements } from '@/hooks/useEntitlements';
import type {
  BrandVoice,
  BrandVoiceUseCase,
  BrandVoiceSource,
  SamplePost,
  PlatformGuideline,
} from '@shared/types/database';
import toast from 'react-hot-toast';
import { getDefaultVoiceColor } from '@/lib/voiceColor';

// =====================================================
// Types
// =====================================================

export type EmojiUsage = 'none' | 'minimal' | 'moderate' | 'frequent';

/**
 * Form data for creating/updating brand voices
 */
export interface BrandVoiceFormData {
  name: string;
  use_case: BrandVoiceUseCase;
  source?: BrandVoiceSource;
  is_default?: boolean;
  color?: string;
  tone?: string[];
  keywords?: string[];
  words_to_avoid?: string[];
  target_audience?: string;
  sample_posts?: SamplePost[];
  platform_guidelines?: Record<string, PlatformGuideline>;
  default_hashtags?: string[];
  emoji_usage?: EmojiUsage;
  call_to_action_style?: string;
  language?: string;
  test_scenario?: string;
}

/**
 * Input for creating a new brand voice
 */
export interface CreateBrandVoiceInput extends BrandVoiceFormData {
  brand_id: string;
}

/**
 * Result from testing a brand voice
 */
export interface TestVoiceResult {
  content: string;
  hashtags: string[];
  warnings?: string[];
}

/**
 * Brand voice with brand relation
 */
export interface BrandVoiceWithBrand extends BrandVoice {
  brand?: {
    id: string;
    name: string;
  };
}

/**
 * Custom error for voice limit reached
 */
export class VoiceLimitReachedError extends Error {
  constructor(currentCount: number, limit: number) {
    super(
      `Brand voice limit reached (${currentCount}/${limit}). Upgrade your plan to add more voices.`
    );
    this.name = 'VoiceLimitReachedError';
  }
}

// =====================================================
// Query: Get All Voices for Brand
// =====================================================

/**
 * Fetch all voices for a specific brand
 * Ordered by is_default DESC, created_at ASC
 */
export function useBrandVoices(brandId: string | undefined) {
  return useQuery({
    queryKey: ['brand-voices', brandId],
    queryFn: async (): Promise<BrandVoice[]> => {
      if (!brandId) return [];

      const { data, error } = await supabase
        .from('brand_voices')
        .select('*')
        .eq('brand_id', brandId)
        .is('deleted_at', null)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: true });

      if (error) {
        console.error('[useBrandVoices] Error:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!brandId,
  });
}

// =====================================================
// Query: Get Single Voice
// =====================================================

/**
 * Fetch single voice by ID with brand relation
 */
export function useBrandVoice(voiceId: string | undefined) {
  return useQuery({
    queryKey: ['brand-voice', voiceId],
    queryFn: async (): Promise<BrandVoiceWithBrand | null> => {
      if (!voiceId) return null;

      const { data, error } = await supabase
        .from('brand_voices')
        .select(
          `
          *,
          brand:brands!brand_voices_brand_id_fkey(id, name)
        `
        )
        .eq('id', voiceId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        console.error('[useBrandVoice] Error:', error);
        throw error;
      }

      return data as BrandVoiceWithBrand;
    },
    enabled: !!voiceId,
  });
}

// =====================================================
// Query: Count Company Voices
// =====================================================

/**
 * Count total voices across all brands for the company
 * Used for limit checking
 */
export function useCompanyVoicesCount() {
  const { data: company } = useCurrentCompany();

  return useQuery({
    queryKey: ['brand-voices-count', company?.id],
    queryFn: async (): Promise<number> => {
      if (!company?.id) return 0;

      const { count, error } = await supabase
        .from('brand_voices')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', company.id)
        .is('deleted_at', null);

      if (error) {
        console.error('[useCompanyVoicesCount] Error:', error);
        throw error;
      }

      return count || 0;
    },
    enabled: !!company?.id,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// =====================================================
// Limit Checking Utilities
// =====================================================

/**
 * Check if user can create a new voice
 */
export function useCanCreateVoice() {
  const { data: count, isLoading: countLoading } = useCompanyVoicesCount();
  const { entitlements, isLoading: entitlementsLoading } = useEntitlements();

  const currentCount = count || 0;
  const limit = entitlements.limits.brandVoices?.max ?? null;
  const canCreate = limit === null || currentCount < limit;

  return {
    canCreate,
    currentCount,
    limit,
    isLoading: countLoading || entitlementsLoading,
  };
}

/**
 * Get detailed brand voice limit info for UI display
 */
export function useBrandVoiceLimitInfo() {
  const { data: count, isLoading: countLoading } = useCompanyVoicesCount();
  const { entitlements, isLoading: entitlementsLoading } = useEntitlements();

  const used = count || 0;
  const limit = entitlements.limits.brandVoices?.max ?? null;
  const remaining = limit === null ? null : Math.max(0, limit - used);
  const planName = entitlements.planName;

  return {
    used,
    limit,
    remaining,
    planName,
    isLoading: countLoading || entitlementsLoading,
  };
}

// =====================================================
// Mutation: Create Brand Voice
// =====================================================

/**
 * Create a new brand voice
 * Checks limit before creating
 */
export function useCreateBrandVoice() {
  const queryClient = useQueryClient();
  const { data: company } = useCurrentCompany();
  const { canCreate, currentCount, limit } = useCanCreateVoice();

  return useMutation({
    mutationFn: async (input: CreateBrandVoiceInput): Promise<BrandVoice> => {
      // Check limit before creating
      if (!canCreate && limit !== null) {
        throw new VoiceLimitReachedError(currentCount, limit);
      }

      if (!company?.id) {
        throw new Error('No company selected');
      }

      const { data, error } = await supabase
        .from('brand_voices')
        .insert({
          brand_id: input.brand_id,
          company_id: company.id,
          name: input.name,
          use_case: input.use_case,
          source: input.source || 'manual',
          is_default: input.is_default ?? false,
          ...(input.color !== undefined && { color: input.color }),
          tone: input.tone || [],
          keywords: input.keywords || [],
          words_to_avoid: input.words_to_avoid || [],
          target_audience: input.target_audience || null,
          sample_posts: input.sample_posts || [],
          platform_guidelines: input.platform_guidelines || {},
          default_hashtags: input.default_hashtags || [],
          emoji_usage: input.emoji_usage || 'moderate',
          call_to_action_style: input.call_to_action_style || null,
          language: input.language || 'en-US',
          test_scenario: input.test_scenario || null,
        })
        .select()
        .single();

      if (error) {
        console.error('[useCreateBrandVoice] Error:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brand-voices', data.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['brand-voices-count'] });
      queryClient.invalidateQueries({ queryKey: ['entitlements'] });
      toast.success(`Voice "${data.name}" created`);
    },
    onError: (error: Error) => {
      if (error instanceof VoiceLimitReachedError) {
        toast.error(error.message, {
          duration: 5000,
          icon: '🔒',
        });
      } else {
        toast.error(`Failed to create voice: ${error.message}`);
      }
    },
  });
}

// =====================================================
// Mutation: Update Brand Voice
// =====================================================

/**
 * Update an existing brand voice
 */
export function useUpdateBrandVoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<BrandVoiceFormData> & { id: string }): Promise<BrandVoice> => {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are provided
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.use_case !== undefined) updateData.use_case = updates.use_case;
      if (updates.source !== undefined) updateData.source = updates.source;
      if (updates.is_default !== undefined) updateData.is_default = updates.is_default;
      if (updates.color !== undefined) updateData.color = updates.color;
      if (updates.tone !== undefined) updateData.tone = updates.tone;
      if (updates.keywords !== undefined) updateData.keywords = updates.keywords;
      if (updates.words_to_avoid !== undefined) updateData.words_to_avoid = updates.words_to_avoid;
      if (updates.target_audience !== undefined) updateData.target_audience = updates.target_audience;
      if (updates.sample_posts !== undefined) updateData.sample_posts = updates.sample_posts;
      if (updates.platform_guidelines !== undefined)
        updateData.platform_guidelines = updates.platform_guidelines;
      if (updates.default_hashtags !== undefined) updateData.default_hashtags = updates.default_hashtags;
      if (updates.emoji_usage !== undefined) updateData.emoji_usage = updates.emoji_usage;
      if (updates.call_to_action_style !== undefined)
        updateData.call_to_action_style = updates.call_to_action_style;
      if (updates.language !== undefined) updateData.language = updates.language;
      if (updates.test_scenario !== undefined) updateData.test_scenario = updates.test_scenario;

      const { data, error } = await supabase
        .from('brand_voices')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('[useUpdateBrandVoice] Error:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['brand-voices', data.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['brand-voice', data.id] });
      toast.success(`Voice "${data.name}" updated`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update voice: ${error.message}`);
    },
  });
}

// =====================================================
// Mutation: Delete Brand Voice
// =====================================================

/**
 * Soft delete a brand voice
 */
export function useDeleteBrandVoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      brandId,
    }: {
      id: string;
      brandId: string;
    }): Promise<{ id: string; brandId: string }> => {
      const { error } = await supabase
        .from('brand_voices')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        console.error('[useDeleteBrandVoice] Error:', error);
        throw error;
      }

      return { id, brandId };
    },
    onSuccess: ({ brandId }) => {
      queryClient.invalidateQueries({ queryKey: ['brand-voices', brandId] });
      queryClient.invalidateQueries({ queryKey: ['brand-voices-count'] });
      queryClient.invalidateQueries({ queryKey: ['entitlements'] });
      toast.success('Voice deleted');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete voice: ${error.message}`);
    },
  });
}

// =====================================================
// Mutation: Test Brand Voice
// =====================================================

/**
 * Test a brand voice by generating sample content
 */
export function useTestBrandVoice() {
  return useMutation({
    mutationFn: async ({
      voiceSettings,
      scenario,
      platform = 'linkedin',
    }: {
      voiceSettings: Partial<BrandVoiceFormData>;
      scenario: string;
      platform?: string;
    }): Promise<TestVoiceResult> => {
      const { data: { session }, error: authError } = await supabase.auth.refreshSession();
      if (authError || !session) throw new Error('Not authenticated');

      const url = getEdgeFunctionUrl('generate-social-draft');
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          eventName: scenario,
          eventDate: new Date().toISOString(),
          eventType: 'test',
          platforms: [platform],
          voiceOverride: {
            tone: voiceSettings.tone || [],
            keywords: voiceSettings.keywords || [],
            words_to_avoid: voiceSettings.words_to_avoid || [],
            target_audience: voiceSettings.target_audience,
            emoji_usage: voiceSettings.emoji_usage || 'moderate',
            call_to_action_style: voiceSettings.call_to_action_style,
            language: voiceSettings.language || 'en-US',
          },
          additionalContext: `Testing brand voice with scenario: ${scenario}`,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      return {
        content: data?.content || '',
        hashtags: data?.hashtags || [],
        warnings: data?.warnings,
      };
    },
    onError: (error: Error) => {
      toast.error(`Failed to test voice: ${error.message}`);
    },
  });
}

// =====================================================
// Auto-Provisioning Helpers
// =====================================================

/**
 * Use case defaults for auto-provisioned voices
 */
export function getUseCaseDefaults(
  useCase: 'sales' | 'support' | 'social' | 'review_response' | 'feedback_response'
): {
  tone: string[];
  target_audience: string;
} {
  switch (useCase) {
    case 'sales':
      return {
        tone: ['professional', 'confident'],
        target_audience: 'Potential customers and leads',
      };
    case 'support':
      return {
        tone: ['friendly', 'helpful'],
        target_audience: 'Existing customers needing assistance',
      };
    case 'social':
      return {
        tone: ['casual', 'engaging'],
        target_audience: 'Social media followers and community members',
      };
    case 'review_response':
      return {
        tone: ['friendly', 'authentic', 'grateful'],
        target_audience: 'Public reviewers (potential and existing customers reading on Google/Trustpilot/etc.)',
      };
    case 'feedback_response':
      return {
        tone: ['empathetic', 'solution-focused', 'professional'],
        target_audience: 'Existing customers who submitted private feedback through the funnel.',
      };
    default:
      return {
        tone: [],
        target_audience: '',
      };
  }
}

/**
 * Check if company can create a voice (for auto-provisioning)
 * Standalone function that doesn't use hooks
 */
export async function checkVoiceLimitForCompany(companyId: string): Promise<{
  canCreate: boolean;
  currentCount: number;
  limit: number | null;
}> {
  // Get current count
  const { count, error: countError } = await supabase
    .from('brand_voices')
    .select('*', { count: 'exact', head: true })
    .eq('company_id', companyId)
    .is('deleted_at', null);

  if (countError) {
    console.error('[checkVoiceLimitForCompany] Count error:', countError);
    return { canCreate: false, currentCount: 0, limit: null };
  }

  const currentCount = count || 0;

  // Get plan limit
  const { data: planData, error: planError } = await supabase.rpc('get_company_plan', {
    p_company_id: companyId,
  });

  if (planError) {
    console.error('[checkVoiceLimitForCompany] Plan error:', planError);
    // If we can't get the limit, assume unlimited
    return { canCreate: true, currentCount, limit: null };
  }

  const limit = planData?.max_brand_voices ?? null;
  const canCreate = limit === null || currentCount < limit;

  return { canCreate, currentCount, limit };
}

/**
 * Auto-provision a voice for a brand
 * Used when addons are enabled or brands are created
 *
 * @param brandId - The brand to create the voice for
 * @param companyId - The company ID
 * @param useCase - The use case for the voice (sales, support, social)
 * @param voiceName - The name for the new voice
 * @returns The created voice or null if it already exists or limit reached
 */
export async function autoProvisionVoice(
  brandId: string,
  companyId: string,
  useCase: 'sales' | 'support' | 'social' | 'review_response' | 'feedback_response',
  voiceName: string
): Promise<BrandVoice | null> {
  try {
    // Check if voice with this use_case already exists for brand
    const { data: existing, error: existingError } = await supabase
      .from('brand_voices')
      .select('id')
      .eq('brand_id', brandId)
      .eq('use_case', useCase)
      .is('deleted_at', null)
      .maybeSingle();

    if (existingError) {
      console.error('[autoProvisionVoice] Check existing error:', existingError);
      return null;
    }

    if (existing) {
      console.log(`[autoProvisionVoice] Voice with use_case=${useCase} already exists for brand ${brandId}`);
      return null; // Already exists
    }

    // Check plan limit
    const { canCreate } = await checkVoiceLimitForCompany(companyId);
    if (!canCreate) {
      console.log(`[autoProvisionVoice] Company ${companyId} has reached voice limit`);
      return null; // Over limit
    }

    // Get defaults based on use_case
    const defaults = getUseCaseDefaults(useCase);

    // Create the voice
    const { data, error } = await supabase
      .from('brand_voices')
      .insert({
        brand_id: brandId,
        company_id: companyId,
        name: voiceName,
        use_case: useCase,
        source: 'auto' as BrandVoiceSource,
        is_default: useCase === 'sales', // Sales voice is default
        tone: defaults.tone,
        target_audience: defaults.target_audience,
        keywords: [],
        words_to_avoid: [],
        sample_posts: [],
        platform_guidelines: {},
        default_hashtags: [],
        emoji_usage: 'moderate',
        call_to_action_style: null,
        language: 'en-US',
        test_scenario: null,
      })
      .select()
      .single();

    if (error) {
      console.error('[autoProvisionVoice] Insert error:', error);
      return null;
    }

    // Apply deterministic default color now that we know the row's id (D-02).
    // The DB DEFAULT '#3B82F6' applied during INSERT - overwrite with the
    // hash-derived color so every voice has a unique-ish stable color.
    const deterministicColor = getDefaultVoiceColor(data.id);
    const { data: updated, error: colorError } = await supabase
      .from('brand_voices')
      .update({ color: deterministicColor })
      .eq('id', data.id)
      .select()
      .single();

    if (colorError) {
      console.warn('[autoProvisionVoice] Color update failed (non-fatal):', colorError);
      // Return the row without the color update - DB DEFAULT keeps it valid.
      console.log(`[autoProvisionVoice] Created ${useCase} voice "${voiceName}" for brand ${brandId}`);
      return data;
    }

    console.log(`[autoProvisionVoice] Created ${useCase} voice "${voiceName}" for brand ${brandId} with color ${deterministicColor}`);
    return updated;
  } catch (error) {
    console.error('[autoProvisionVoice] Unexpected error:', error);
    return null;
  }
}

/**
 * Get the default brand for a company
 * Used for auto-provisioning when addon is enabled
 */
export async function getDefaultBrandForCompany(companyId: string): Promise<{ id: string; name: string } | null> {
  const { data, error } = await supabase
    .from('brands')
    .select('id, name')
    .eq('company_id', companyId)
    .eq('is_default', true)
    .is('deleted_at', null)
    .maybeSingle();

  if (error) {
    console.error('[getDefaultBrandForCompany] Error:', error);
    return null;
  }

  return data;
}

// =====================================================
// Re-exports for backward compatibility
// =====================================================

// Re-export tone options from brand-voice.ts
export { TONE_OPTIONS, TONE_GROUPS, EMOJI_USAGE_OPTIONS, PLATFORM_CONFIGS, checkPlatformLimits } from './brand-voice';
export type { SupportedPlatform } from './brand-voice';
